import { useMemo, useState } from 'react';
import { filterTriageThreads, sortTriageThreads, type TriageFilters } from '../lib/clinic-logic';
import type { EmailThreadMetadata, LabelCategoryMapping, TriageCategory } from '../types/email';

export function useTriageFilters(
  threads: EmailThreadMetadata[],
  labelWeights: Record<string, number> = {},
  domainWeights: Record<string, number> = {},
  categories: TriageCategory[] = [],
  mappings: LabelCategoryMapping[] = []
) {
  const [filters, setFilters] = useState<TriageFilters>({
    domain: 'all',
    difficulty: 'all',
    state: 'all',
    label: 'all',
    hasDeadline: false,
    category: 'all',
    showResolved: false,
    sortBy: 'importance',
    sortOrder: 'desc',
  });

  const setFilter = <K extends keyof TriageFilters>(key: K, value: TriageFilters[K]) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetFilters = () => {
    setFilters({
      domain: 'all',
      difficulty: 'all',
      state: 'all',
      label: 'all',
      hasDeadline: false,
      category: 'all',
      showResolved: false,
      sortBy: 'importance',
      sortOrder: 'desc',
    });
  };

  const filteredThreads = useMemo(() => {
    const filtered = filterTriageThreads(threads, filters, mappings);
    return sortTriageThreads(filtered, filters, labelWeights, domainWeights, categories, mappings);
  }, [threads, filters, labelWeights, domainWeights, categories, mappings]);

  return {
    filters,
    filteredThreads,
    setFilter,
    resetFilters,
  };
}
